import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import {
  space,
  Avatar,
  Text,
  color,
  radius,
  BaseButton,
  device,
  fontWeight,
  fontSize,
  H4,
} from '@ticketswap/solar'
import TraderInformationDialog from '~/components/TraderInformationDialog'
import { CheckmarkRounded } from '@ticketswap/comets'

const Container = styled.div`
  position: relative;
  background-color: ${color.nova};
  border-radius: ${radius.lg};
  display: grid;
  grid-template-columns: 0.75fr 1fr;
  align-items: center;
  padding: ${space[32]};
  border: 1px solid ${color.spaceLightest};

  @media ${device.tablet} {
    grid-template-columns: 1fr 1fr;

    ${props =>
      props.isSecureSwapAvailable &&
      css`
        grid-template-columns: 0.5fr 1fr;
        grid-gap: ${space[24]};
      `};
  }
`

const StyledAvatar = styled(Avatar)`
  width: ${space[48]};
  height: ${space[48]};
  min-height: ${space[48]};
  min-width: ${space[48]};
  margin-bottom: ${space[8]};

  @media ${device.tablet} {
    width: ${space[56]};
    height: ${space[56]};
    min-height: ${space[56]};
    min-width: ${space[56]};
  }
`

const NameAndAvatar = styled.div`
  align-self: center;
  justify-self: center;
  text-align: center;

  h4 {
    font-weight: ${fontWeight.semiBold};
  }

  em {
    color: ${color.spaceMedium};
    font-style: normal;
    margin-top: -4px;
    display: block;
  }

  @media ${device.tablet} {
    justify-self: flex-end;
    ${props =>
      !props.isSecureSwapAvailable &&
      css`
        padding-right: ${space[64]};
      `};
  }
`

const Verifications = styled.ul`
  align-self: center;
  justify-self: center;

  @media ${device.tablet} {
    justify-self: ${props => (props.isSecureSwapAvailable ? 'flex-end' : 'flex-start')};
  }
`

const VerificationsItem = styled.li`
  position: relative;
  padding-left: ${space[32]};
  font-size: ${fontSize[14]};

  button {
    font-size: ${fontSize[14]};
  }

  & + ${() => VerificationsItem} {
    margin-top: ${space[4]};
  }
  em {
    color: ${color.spaceLight};
    font-style: normal;
    display: block;
  }

  .icon {
    position: absolute;
    left: 0;
    top: 2px;
    color: ${color.titan};

    width: ${space[16]};
    height: ${space[16]};
    min-height: ${space[16]};
    min-width: ${space[16]};
  }

  @media ${device.tablet} {
    font-size: ${fontSize[16]};
    padding-left: ${space[24]};

    button {
      font-size: ${fontSize[16]};
    }

    .icon {
      width: ${space[16]};
      height: ${space[16]};
      min-height: ${space[16]};
      min-width: ${space[16]};
    }

    & + ${() => VerificationsItem} {
      margin-top: ${space[16]};
    }
  }
`

function SellerInformation({ t, seller = {}, showLastname, ...props }) {
  return (
    <Container {...props}>
      <NameAndAvatar isSecureSwapAvailable={props.isSecureSwapAvailable}>
        <StyledAvatar src={seller.avatarBig} size={64} />
        <H4>
          {showLastname && seller.lastname
            ? `${seller.firstname} ${seller.lastname}`
            : seller.firstname}
        </H4>
        <Text>
          <em>{seller.city}</em>
        </Text>
      </NameAndAvatar>
      <Verifications isSecureSwapAvailable={props.isSecureSwapAvailable}>
        {seller.successfullySoldCount > 0 && (
          <VerificationsItem>
            <CheckmarkRounded size={24} />
            {t('listing:purchaser.ticketsSold', {
              count: seller.successfullySoldCount,
            })}

            {seller.successfullySoldCount > 100 && (
              <TraderInformationDialog t={t} {...props}>
                {({ getToggleProps }) => (
                  <em>
                    <BaseButton {...getToggleProps()}>
                      {t('listing:purchaser.whySoMany')}
                    </BaseButton>
                  </em>
                )}
              </TraderInformationDialog>
            )}
          </VerificationsItem>
        )}

        {seller.facebookAccount && (
          <VerificationsItem>
            <CheckmarkRounded size={24} />
            {t('listing:purchaser.facebookConnected')}{' '}
            {seller.facebookAccount?.friendsCount > 0 && (
              <em>
                {t('listing:purchaser.friend', {
                  count: seller.facebookAccount.friendsCount,
                })}
              </em>
            )}
          </VerificationsItem>
        )}

        <VerificationsItem>
          <CheckmarkRounded size={24} />
          {t('listing:purchaser.phoneVerified')}
        </VerificationsItem>
      </Verifications>
    </Container>
  )
}

SellerInformation.defaultProps = {
  showLastname: true,
}

SellerInformation.propTypes = {
  t: PropTypes.func.isRequired,
  showLastname: PropTypes.bool.isRequired,
  seller: PropTypes.shape({
    avatar: PropTypes.string.isRequired,
    avatarBig: PropTypes.string.isRequired,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string,
    city: PropTypes.string.isRequired,
    successfullySoldCount: PropTypes.number.isRequired,
    facebookAccount: PropTypes.shape({
      friendsCount: PropTypes.number.isRequired,
    }),
  }),
}

export default SellerInformation
